import { useCallback, useEffect, useRef, useState, type FocusEvent, type PointerEvent } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { RoleLabel, type Role } from "@probetalab/contracts";
import { useSession } from "../lib/session.js";
import { Icon, Mark, type IconName } from "../components/icons.js";
import { fmtDateLong } from "../lib/format.js";
import { get } from "../lib/api.js";
import { useQuery } from "../lib/useQuery.js";
import { PENDING_SAMPLES_CHANGED } from "./PendingSamples.js";
import { ChatProvider } from "../lib/chat.js";
import { ChatButton, ChatPanel } from "../components/ChatBubble.js";

/** Sidebar entries per role (functional scope §2). Roles are additive; what a role cannot do is not shown. */
interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  minimum: Role;
  end?: boolean;
  /** Shows the pending-tube count of the current branch. */
  badge?: "pendingSamples";
}
const GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: "Mostrador",
    items: [
      // Inicio (Very, 2026-11-03): the landing after sign-in; the sidebar mark links here too.
      { to: "/inicio", label: "Inicio", icon: "home", minimum: "recepcion" },
      { to: "/", label: "Solicitudes", icon: "list", minimum: "recepcion", end: true },
      { to: "/recepcion", label: "Recepción", icon: "userPlus", minimum: "recepcion" },
      // At the counter: the receptionist records the collection (sample.event is Recepción's).
      { to: "/muestras", label: "Muestras pendientes", icon: "dropper", minimum: "recepcion", badge: "pendingSamples" },
      { to: "/cotizaciones", label: "Cotizaciones", icon: "quote", minimum: "recepcion" },
      { to: "/pacientes", label: "Pacientes", icon: "users", minimum: "recepcion" },
      { to: "/entrega", label: "Entrega", icon: "send", minimum: "recepcion" },
      { to: "/calendario", label: "Calendario", icon: "calendar", minimum: "recepcion" },
    ],
  },
  {
    title: "Laboratorio",
    items: [
      { to: "/captura", label: "Captura", icon: "flask", minimum: "quimico" },
      { to: "/validacion", label: "Validación", icon: "badge", minimum: "quimico" },
    ],
  },
  {
    title: "Administración",
    items: [
      { to: "/caja", label: "Corte de caja", icon: "cash", minimum: "recepcion" },
      { to: "/reportes/medicos", label: "Por médico", icon: "stethoscope", minimum: "quimico" },
      { to: "/catalogo", label: "Catálogo", icon: "book", minimum: "quimico" },
      { to: "/configuracion", label: "Configuración", icon: "settings", minimum: "administrador" },
    ],
  },
];

/**
 * Compact sidebar (pilot feedback 2026-11-02: the full sidebar takes too much room on a desktop
 * PC). A laboratory setting (`lab_settings.compact_sidebar`, Configuración → Preferencias, off
 * by default): when on, wide screens (> 1100 px) open the sidebar as the same 84 px icon rail the
 * tablet width always uses, and a toggle lets each user expand it; that personal choice is
 * remembered per device. When off, nothing changes: no toggle, the full sidebar.
 */
export const SIDEBAR_KEY = "probeta.sidebar";
type SidebarChoice = "collapsed" | "expanded";

function readStoredChoice(): SidebarChoice | null {
  try {
    const v = localStorage.getItem(SIDEBAR_KEY);
    return v === "collapsed" || v === "expanded" ? v : null;
  } catch {
    return null;
  }
}

/** Whether the sidebar is drawn as an icon rail right now (mirrors the breakpoints in styles.css). */
function railShowing(collapsed: boolean): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return collapsed;
  if (window.matchMedia("(max-width: 720px)").matches) return false; // phone drawer: labels are visible
  if (window.matchMedia("(max-width: 1100px)").matches) return true; // tablet: always a rail
  return collapsed;
}

function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
}

interface Tip {
  label: string;
  top: number;
  left: number;
}

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function Shell() {
  const { me, branch, logout, can, isOperator, switchTenant } = useSession();
  const navigate = useNavigate();
  const now = useClock();
  const { pathname } = useLocation();
  // Phones only (styles.css ≤ 720 px): the sidebar becomes a drawer behind a menu button.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Off for the laboratory: the full sidebar, no toggle, whatever this device stored. On: the
  // rail by default, and the user's own choice on this device wins once they toggle.
  const labCompact = me?.lab_settings?.compact_sidebar ?? false;
  const [stored, setStored] = useState(readStoredChoice);
  const collapsed = labCompact && stored !== "expanded";
  const toggleCollapsed = () => {
    const next: SidebarChoice = collapsed ? "expanded" : "collapsed";
    setStored(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, next);
    } catch {
      /* storage unavailable: the choice lasts for this page only */
    }
  };

  // Rail flyout: one dark pill to the right of the hovered or keyboard-focused item. It is fixed
  // to the viewport so the sidebar's own scroll box does not clip it. Touch never depends on it:
  // a tap navigates as before and does not open the pill.
  const [tip, setTip] = useState<Tip | null>(null);
  const touchAt = useRef(0);
  const hideTip = useCallback(() => setTip(null), []);
  const showTip = (target: EventTarget | null) => {
    const el = target instanceof Element ? target.closest<HTMLElement>("[data-tip]") : null;
    if (!el?.dataset.tip || !labCompact || !railShowing(collapsed)) return setTip(null);
    const r = el.getBoundingClientRect();
    setTip({ label: el.dataset.tip, top: r.top + r.height / 2, left: r.right + 10 });
  };
  const onPointerOver = (e: PointerEvent) => {
    if (e.pointerType === "mouse") showTip(e.target);
  };
  const onPointerOut = (e: PointerEvent) => {
    const from = e.target instanceof Element ? e.target.closest("[data-tip]") : null;
    if (from && !(e.relatedTarget instanceof Node && from.contains(e.relatedTarget))) hideTip();
  };
  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") touchAt.current = Date.now();
    hideTip();
  };
  const onFocus = (e: FocusEvent) => {
    if (Date.now() - touchAt.current < 800) return; // focus caused by a tap
    showTip(e.target);
  };
  useEffect(hideTip, [pathname, collapsed, hideTip]);
  useEffect(() => {
    if (!tip) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && hideTip();
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", hideTip);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", hideTip);
    };
  }, [tip, hideTip]);

  // One count query for the badge: on each screen change, and when Muestras pendientes changes a tube.
  const pending = useQuery(() => get("pendingSamplesCount", { query: { branch_id: branch!.id } }), [branch?.id, pathname], { enabled: !!branch });
  useEffect(() => {
    const onChange = () => void pending.reload();
    window.addEventListener(PENDING_SAMPLES_CHANGED, onChange);
    return () => window.removeEventListener(PENDING_SAMPLES_CHANGED, onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const pendingCount = pending.data?.count ?? 0;
  if (!me || !branch) return null;
  const roleText = isOperator ? "Soporte Probeta Lab" : RoleLabel[me.user.role];
  const toggleLabel = collapsed ? "Expandir menú" : "Contraer menú";

  return (
    <ChatProvider enabled={!isOperator} branchId={branch.id}>
    <div
      className={["shell", isOperator ? "support" : "", menuOpen ? "menu-open" : "", labCompact ? "lab-compact" : "", collapsed ? "collapsed" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <button type="button" className="scrim" aria-hidden="true" tabIndex={-1} onClick={() => setMenuOpen(false)} />
      <aside
        className="sidebar"
        id="app-sidebar"
        onPointerOver={onPointerOver}
        onPointerOut={onPointerOut}
        onPointerDown={onPointerDown}
        onFocus={onFocus}
        onBlur={hideTip}
        onScroll={hideTip}
      >
        <div className="sidebar-head">
          <Link to="/inicio" className="brand" aria-label={`Probeta Lab, ${me.tenant.name}`} data-tip={me.tenant.name}>
            <Mark />
            <span className="text">
              <span className="name">Probeta Lab</span>
              <br />
              <span className="sub">{me.tenant.name}</span>
            </span>
          </Link>
          {labCompact && (
            <button
              type="button"
              className="rail-toggle"
              aria-label={toggleLabel}
              title={collapsed ? undefined : toggleLabel}
              data-tip={toggleLabel}
              onClick={toggleCollapsed}
            >
              <Icon name={collapsed ? "chevronRight" : "chevronLeft"} />
            </button>
          )}
        </div>
        <nav className="nav" aria-label="Secciones">
          {GROUPS.map((g) => {
            const items = g.items.filter((n) => can(n.minimum));
            if (!items.length) return null;
            return (
              <div key={g.title}>
                <div className="nav-group">{g.title}</div>
                {items.map((n) => {
                  const badge = n.badge === "pendingSamples" && pendingCount > 0 ? (pendingCount > 99 ? "99+" : String(pendingCount)) : null;
                  // The name survives the icon rail, where the label span is not displayed.
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      aria-label={badge ? `${n.label} ${badge}` : n.label}
                      data-tip={badge ? `${n.label} · ${badge}` : n.label}
                      className={({ isActive }) => (isActive ? "active" : "")}
                    >
                      <Icon name={n.icon} />
                      <span>{n.label}</span>
                      {badge && <b className="nav-badge">{badge}</b>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>
        {/* The chat bubble sits right above the user block: never over page content (ADR 0028). */}
        <div className="chat-slot">
          <ChatButton variant="rail" />
        </div>
        <div className="sidebar-foot">
          <div className="who">
            <span className="name">{me.user.full_name}</span>
            <span className="role">{roleText}</span>
          </div>
          {/* Rail only: the initials stand in for the name, which stays in .who for screen readers. */}
          {labCompact && (
            <span className="who-mini" aria-hidden="true" data-tip={`${me.user.full_name} · ${roleText}`}>
              {initialsOf(me.user.full_name)}
            </span>
          )}
          {!isOperator && me.user.can_validate && (
            <NavLink
              to="/mi-firma"
              aria-label="Mi firma"
              title={labCompact ? undefined : "Mi firma"}
              data-tip="Mi firma"
              className={({ isActive }) => (isActive ? "active" : "")}
            >
              <Icon name="pen" />
              <span>Mi firma</span>
            </NavLink>
          )}
          <button type="button" aria-label="Cerrar sesión" data-tip="Cerrar sesión" onClick={() => void logout()}>
            <Icon name="logout" />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </aside>
      {tip && (
        <div className="rail-tip" aria-hidden="true" style={{ top: tip.top, left: tip.left }}>
          {tip.label}
        </div>
      )}
      <div className="main">
        {isOperator && me.operator && (
          <div className="support-banner" role="status">
            <Icon name="badge" />
            <span>
              <strong>Acceso de soporte<span className="long"> de Probeta Lab</span></strong>
              <span className="long"> — {me.tenant.name}</span>
            </span>
            {me.operator.platform_login.enabled && (
              <span className="support-pill" title={`El personal de ${me.tenant.name} puede iniciar sesión en ${me.operator.platform_login.platform_host}`}>
                Personal por {me.operator.platform_login.platform_host}
              </span>
            )}
            {/* On a laboratory's own host the session stands in that laboratory only (ADR 0025). */}
            {!me.operator.host_bound && me.operator.tenants.length > 1 && (
              <label className="tenant-switch">
                <span className="sr-only">Laboratorio</span>
                <select value={me.tenant.id} onChange={(e) => void switchTenant(e.target.value)} aria-label="Laboratorio">
                  {me.operator.tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        <header className="topbar">
          <button
            type="button"
            className="menu-btn btn-icon"
            aria-label="Menú"
            aria-controls="app-sidebar"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <Icon name="menu" />
          </button>
          <button type="button" className="branch-btn" onClick={() => navigate("/sucursal")} title="Cambiar de sucursal">
            <span className="code">{branch.short_code}</span>
            <span className="name">{branch.name}</span>
            <Icon name="chevronDown" />
          </button>
          <span className="clock">
            <b>{now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", timeZone: "America/Mexico_City" })}</b>
            <span className="date"> · {fmtDateLong(now)}</span>
          </span>
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
      <ChatButton variant="float" />
      <ChatPanel />
    </div>
    </ChatProvider>
  );
}
